import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet/Sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/AlertDialog/AlertDialog";
import { Separator } from "@/components/ui/Separator/Separator";
import { VStack } from "@/components/layout/VStack";
import { HStack } from "@/components/layout/HStack";
import { useEffect, useState } from "react";
import type { ApiUser } from "@shared/types/models";

const EditUserSheet = ({
  user,
  open,
  onOpenChange,
  isSelf,
  saving,
  deleting,
  onSave,
  onDelete,
}: {
  user: ApiUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSelf: boolean;
  saving: boolean;
  deleting: boolean;
  onSave: (patch: { name: string; gradYear: number | null }) => Promise<void>;
  onDelete: () => Promise<void>;
}) => {
  const [name, setName] = useState(user.name);
  const [gradYear, setGradYear] = useState<string>(
    user.gradYear != null ? String(user.gradYear) : "",
  );

  useEffect(() => {
    if (open) {
      setName(user.name);
      setGradYear(user.gradYear != null ? String(user.gradYear) : "");
    }
  }, [open, user]);

  const trimmedName = name.trim();
  const parsedGradYear = gradYear === "" ? null : Number(gradYear);
  const gradYearInvalid =
    parsedGradYear !== null &&
    (!Number.isInteger(parsedGradYear) ||
      parsedGradYear < 1900 ||
      parsedGradYear > 2100);

  const isDirty =
    trimmedName !== user.name ||
    parsedGradYear !== (user.gradYear ?? null);

  const canSave =
    !saving && trimmedName.length > 0 && !gradYearInvalid && isDirty;

  const handleSave = async () => {
    if (!canSave) return;
    await onSave({ name: trimmedName, gradYear: parsedGradYear });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>Edit {user.name}</SheetTitle>
          <SheetDescription>
            Update name and grad year. Email, role, and login identity cannot
            be changed here.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <VStack gap="2px">
            <Label>Email</Label>
            <Input value={user.email} disabled readOnly />
          </VStack>
          <VStack gap="2px">
            <Label>Role</Label>
            <Input value={user.role} disabled readOnly />
          </VStack>
          <Separator />
          <VStack gap="2px">
            <Label htmlFor="edit-user-name">Name</Label>
            <Input
              id="edit-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </VStack>
          <VStack gap="2px">
            <Label htmlFor="edit-user-grad-year">Grad year</Label>
            <Input
              id="edit-user-grad-year"
              type="number"
              inputMode="numeric"
              value={gradYear}
              min={1900}
              max={2100}
              onChange={(e) => setGradYear(e.target.value)}
              placeholder="Leave blank to clear"
            />
            {gradYearInvalid && (
              <span
                style={{
                  color: "var(--destructive)",
                  fontSize: "var(--text-sm)",
                }}
              >
                Grad year must be an integer between 1900 and 2100.
              </span>
            )}
          </VStack>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            style={{ width: "100%" }}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
          {!isSelf && (
            <>
              <Separator />
              <VStack gap="4px">
                <Label>Danger zone</Label>
                <p
                  style={{
                    color: "var(--muted-foreground)",
                    fontSize: "var(--text-sm)",
                  }}
                >
                  Deleting this user is permanent. Any students they advise
                  will have their advisor cleared.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      style={{ width: "100%" }}
                      disabled={deleting}
                    >
                      {deleting ? "Deleting..." : "Delete user"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete {user.name}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes {user.email}. This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <HStack gap="0.5rem" justify="flex-end">
                        <AlertDialogCancel asChild>
                          <Button variant="outline">Cancel</Button>
                        </AlertDialogCancel>
                        <AlertDialogAction asChild>
                          <Button
                            variant="destructive"
                            onClick={onDelete}
                            disabled={deleting}
                          >
                            Delete
                          </Button>
                        </AlertDialogAction>
                      </HStack>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </VStack>
            </>
          )}
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline" style={{ width: "100%" }}>
              Close
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export { EditUserSheet };
